// FAQ.jsx
// ─ Modern clean FAQ section matching NoticeBoard / Reservation design
// ─ No heavy cartoon UI, softer premium outdoor style
// ─ Pure Tailwind + Framer Motion

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { FiChevronDown } from 'react-icons/fi';

/* ─────────────────────────────────────────────
   FAQ DATA
───────────────────────────────────────────── */
const FAQ_DATA = [
  {
    qJa: 'バギーは初めてでも大丈夫ですか？',
    qEn: 'Is it okay if it’s my first time riding a buggy?',
    aJa: 'もちろんです！初回は必ず講習（¥500）を受けていただきます。スタッフが丁寧にサポートしますので、初心者の方でも安心です。',
    aEn: 'Absolutely. First-time riders receive a short lesson (¥500), and our staff will guide you carefully.',
  },
  {
    qJa: '外国人でも参加できますか？',
    qEn: 'Can international visitors participate?',
    aJa: 'はい、英語対応可能です。海外からのお客様も安心してご参加いただけます。',
    aEn: 'Yes. English support is available and international visitors are welcome.',
  },
  {
    qJa: '服装や持ち物は何が必要ですか？',
    qEn: 'What should I wear and bring?',
    aJa: '動きやすい服装と汚れても良い靴がおすすめです。ヘルメットなど必要な装備はすべてご用意しています。',
    aEn: 'Comfortable clothes and shoes you don’t mind getting dirty are recommended. Safety equipment is provided.',
  },
  {
    qJa: '予約は必要ですか？',
    qEn: 'Do I need a reservation?',
    aJa: '予約なしでもご利用いただけますが、週末や連休は事前予約がおすすめです。',
    aEn: 'Walk-ins are welcome, but reservations are recommended during busy weekends and holidays.',
  },
  {
    qJa: '駐車場はありますか？',
    qEn: 'Is there parking available?',
    aJa: '無料駐車場を完備しております。',
    aEn: 'Yes. Free parking is available.',
  },
  {
    qJa: '子どもは何歳から参加できますか？',
    qEn: 'What is the minimum age for children?',
    aJa: 'バギー体験は10歳以上が目安です。小さなお子様向け体験もございます。',
    aEn: 'Buggy riding is generally for ages 10 and up. Other family-friendly activities are also available.',
  },
  {
    qJa: '雨の日でも営業していますか？',
    qEn: 'Are you open on rainy days?',
    aJa: '小雨程度であれば営業可能です。悪天候の場合は変更となる場合があります。',
    aEn: 'Light rain is usually okay, but activities may change during severe weather.',
  },
];

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  transition: {
    duration: 0.55,
    delay,
    ease: [0.4, 0, 0.2, 1],
  },
  viewport: { once: true, margin: '-60px' },
});

export default function FAQ() {
  const { i18n } = useTranslation();
  const isJa = i18n.language?.startsWith('ja');

  const [openIdx, setOpenIdx] = useState(0);

  const toggle = (idx) => {
    setOpenIdx(openIdx === idx ? null : idx);
  };

  return (
    <section
      id="faq"
      className="relative overflow-hidden py-24 bg-[#f4f9ee]"
    >
      {/* bg pattern */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            'radial-gradient(circle,rgba(31,77,43,0.04) 1px,transparent 1px)',
          backgroundSize: '30px 30px',
        }}
      />

      <div className="absolute top-10 left-10 text-[120px] opacity-[0.04] pointer-events-none select-none">
        🌿
      </div>

      <div className="absolute bottom-10 right-10 text-[120px] opacity-[0.04] pointer-events-none select-none">
        🍃
      </div>

      <div className="max-w-5xl mx-auto px-5 relative z-10">

        {/* HEADER */}
        <motion.div
          className="text-center mb-14"
          {...fadeUp()}
        >
          <div className="inline-flex items-center gap-2 bg-[#5b3a1c] text-white px-7 py-3 rounded-2xl shadow-lg border-2 border-[#7a5230] mb-5">
            <span className="font-extrabold text-lg tracking-wide">
              {isJa ? 'よくある質問' : 'FAQ'}
            </span>
          </div>

          <h2
            className="font-extrabold text-[#1a3d20] mb-4 leading-[1.05]"
            style={{
              fontSize: 'clamp(2.2rem,6vw,4.5rem)',
            }}
          >
            {isJa
              ? 'よくあるご質問'
              : 'Frequently Asked Questions'}
          </h2>

          <p className="text-[#2d6b38]/70 text-base md:text-lg font-semibold max-w-2xl mx-auto">
            {isJa
              ? 'ご予約・体験内容・アクセスなど、よくいただくご質問をまとめました'
              : 'Answers about reservations, buggy rides, access and more'}
          </p>
        </motion.div>

        {/* FAQ LIST */}
        <div className="flex flex-col gap-4">

          {FAQ_DATA.map((item, idx) => {
            const isOpen = openIdx === idx;

            return (
              <motion.div
                key={idx}
                {...fadeUp(idx * 0.05)}
                className={`
                  bg-[#faf7f0]
                  border-[1.5px]
                  rounded-[24px]
                  overflow-hidden
                  transition-all duration-300
                  ${
                    isOpen
                      ? 'border-[#1f4d2b]/40 shadow-xl shadow-[#1f4d2b]/10'
                      : 'border-[#dfd1bc] shadow-sm'
                  }
                `}
              >
                <button
                  onClick={() => toggle(idx)}
                  className="
                    w-full
                    flex items-center justify-between
                    gap-4
                    px-6 py-5
                    text-left
                    hover:bg-[#f8f3ea]
                    transition-colors
                  "
                >
                  <div className="flex-1">
                    <p className="text-[#173617] font-extrabold text-base md:text-lg leading-snug">
                      {isJa ? item.qJa : item.qEn}
                    </p>
                  </div>

                  <div
                    className={`
                      w-10 h-10 rounded-full
                      flex items-center justify-center
                      shrink-0
                      transition-all duration-300
                      ${
                        isOpen
                          ? 'bg-[#1f4d2b] text-white rotate-180'
                          : 'bg-[#1f4d2b]/10 text-[#1f4d2b]'
                      }
                    `}
                  >
                    <FiChevronDown size={18} />
                  </div>
                </button>

                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{
                        duration: 0.3,
                        ease: [0.4, 0, 0.2, 1],
                      }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-6">
                        <div className="bg-white border border-[#e5dac8] rounded-[20px] p-5">
                          <p className="text-[#555] leading-[1.9] text-sm md:text-[15px] font-semibold">
                            {isJa ? item.aJa : item.aEn}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>

      

      </div>
    </section>
  );
}